import { v4 as uuidv4 } from "uuid";
import { z } from "zod";
import { parseMultilineContent } from "../content/plaintext.js";
import {
  copilotProposalIdSchema,
  trainingProposalKindSchema
} from "./copilotProposal.js";
import {
  moveChapterAfter,
  moveChapterBefore,
  moveLessonAfter,
  moveLessonBefore,
  moveLessonToChapterEnd,
  removeChapter,
  removeLesson
} from "./course.js";
import { InterviewSectionType } from "./interviewSection/types.js";
import { playbookIdSchema } from "./playbook.js";
const questionsSchema = z.array(z.string().min(1)).min(1);
const chatQuizFieldsSchema = z.object({
  topic: z.string().min(1),
  questions: questionsSchema
});
const knowledgeTestFieldsSchema = z.object({
  title: z.string().min(1),
  questions: questionsSchema
});
const rolePlayFieldsSchema = z.object({
  title: z.string().min(1),
  participant_instructions: z.string().min(1),
  agent_instructions: z.string().min(1)
});
const newLessonSchema = z.discriminatedUnion("lesson_type", [
  chatQuizFieldsSchema.extend({
    lesson_type: z.literal(InterviewSectionType.ChatQuiz)
  }),
  knowledgeTestFieldsSchema.extend({
    lesson_type: z.literal(InterviewSectionType.KnowledgeTest)
  }),
  rolePlayFieldsSchema.extend({
    lesson_type: z.literal(InterviewSectionType.RolePlay)
  })
]);
const lessonChangesSchema = z.discriminatedUnion("lesson_type", [
  chatQuizFieldsSchema.partial().extend({
    lesson_type: z.literal(InterviewSectionType.ChatQuiz)
  }),
  knowledgeTestFieldsSchema.partial().extend({
    lesson_type: z.literal(InterviewSectionType.KnowledgeTest)
  }),
  rolePlayFieldsSchema.partial().extend({
    lesson_type: z.literal(InterviewSectionType.RolePlay)
  })
]).refine(
  (changes) => Object.entries(changes).some(
    ([field, value]) => field !== "lesson_type" && value !== void 0
  ),
  "A lesson change names at least one field to change."
);
const relativePositionSchema = z.enum(["before", "after"]);
const trainingProposalOperationSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("add_chapter"),
    topic: z.string().min(1),
    lessons: z.array(newLessonSchema)
  }),
  z.object({
    type: z.literal("update_chapter"),
    chapter_id: z.string(),
    topic: z.string().min(1)
  }),
  z.object({
    type: z.literal("remove_chapter"),
    chapter_id: z.string()
  }),
  z.object({
    type: z.literal("move_chapter"),
    chapter_id: z.string(),
    position: relativePositionSchema,
    target_chapter_id: z.string()
  }),
  z.object({
    type: z.literal("add_lesson"),
    chapter_id: z.string(),
    lesson: newLessonSchema
  }),
  z.object({
    type: z.literal("update_lesson"),
    lesson_id: z.string(),
    changes: lessonChangesSchema
  }),
  z.object({
    type: z.literal("remove_lesson"),
    lesson_id: z.string()
  }),
  z.object({
    type: z.literal("move_lesson"),
    lesson_id: z.string(),
    position: relativePositionSchema,
    target_lesson_id: z.string()
  }),
  z.object({
    type: z.literal("move_lesson_to_chapter"),
    lesson_id: z.string(),
    chapter_id: z.string()
  })
]);
const trainingProposalOperationsSchema = z.array(trainingProposalOperationSchema).min(1);
class TrainingOperationError extends Error {
}
function applyTrainingOperations(training, operations) {
  return operations.reduce(
    (current, operation, index) => applyOperation(
      current,
      operation,
      `Operation ${index + 1} (${operation.type})`
    ),
    training
  );
}
function applyOperation(training, operation, label) {
  const { course, sections } = training;
  switch (operation.type) {
    case "add_chapter": {
      const newSections = operation.lessons.map((lesson) => ({
        id: mintClientId(),
        settings: buildLessonSettings(lesson)
      }));
      const chapter = {
        id: mintClientId(),
        topic: operation.topic,
        lessons: newSections.map(({ id }) => ({
          interviewSectionDefinitionId: id
        }))
      };
      return {
        ...training,
        course: { ...course, chapters: [...course.chapters, chapter] },
        sections: [...sections, ...newSections]
      };
    }
    case "update_chapter": {
      const chapter = findChapter(course, operation.chapter_id, label);
      return {
        ...training,
        course: replaceChapter(course, { ...chapter, topic: operation.topic })
      };
    }
    case "remove_chapter": {
      const chapter = findChapter(course, operation.chapter_id, label);
      const removedIds = new Set(
        chapter.lessons.map((lesson) => lesson.interviewSectionDefinitionId)
      );
      const [courseWithoutChapter] = removeChapter(course, chapter.id);
      return {
        ...training,
        course: courseWithoutChapter,
        sections: sections.filter((section) => !removedIds.has(section.id))
      };
    }
    case "move_chapter": {
      findChapter(course, operation.chapter_id, label);
      findChapter(course, operation.target_chapter_id, label);
      if (operation.chapter_id === operation.target_chapter_id) {
        throw new TrainingOperationError(
          `${label}: chapter ${operation.chapter_id} cannot be moved relative to itself`
        );
      }
      const moveChapter = operation.position === "before" ? moveChapterBefore : moveChapterAfter;
      return {
        ...training,
        course: moveChapter(
          course,
          operation.chapter_id,
          operation.target_chapter_id
        )
      };
    }
    case "add_lesson": {
      const chapter = findChapter(course, operation.chapter_id, label);
      const section = {
        id: mintClientId(),
        settings: buildLessonSettings(operation.lesson)
      };
      return {
        ...training,
        course: replaceChapter(course, {
          ...chapter,
          lessons: [
            ...chapter.lessons,
            { interviewSectionDefinitionId: section.id }
          ]
        }),
        sections: [...sections, section]
      };
    }
    case "update_lesson": {
      assertCourseHasLesson(course, operation.lesson_id, label);
      const section = findSection(training, operation.lesson_id, label);
      const settings = updateLessonSettings(section, operation.changes, label);
      return {
        ...training,
        sections: sections.map(
          (item) => item.id === section.id ? { ...item, settings } : item
        )
      };
    }
    case "remove_lesson": {
      assertCourseHasLesson(course, operation.lesson_id, label);
      const [courseWithoutLesson] = removeLesson(course, operation.lesson_id);
      return {
        ...training,
        course: courseWithoutLesson,
        sections: sections.filter((item) => item.id !== operation.lesson_id)
      };
    }
    case "move_lesson": {
      assertCourseHasLesson(course, operation.lesson_id, label);
      assertCourseHasLesson(course, operation.target_lesson_id, label);
      if (operation.lesson_id === operation.target_lesson_id) {
        throw new TrainingOperationError(
          `${label}: lesson ${operation.lesson_id} cannot be moved relative to itself`
        );
      }
      const moveLesson = operation.position === "before" ? moveLessonBefore : moveLessonAfter;
      return {
        ...training,
        course: moveLesson(
          course,
          operation.lesson_id,
          operation.target_lesson_id
        )
      };
    }
    case "move_lesson_to_chapter": {
      assertCourseHasLesson(course, operation.lesson_id, label);
      findChapter(course, operation.chapter_id, label);
      return {
        ...training,
        course: moveLessonToChapterEnd(
          course,
          operation.lesson_id,
          operation.chapter_id
        )
      };
    }
  }
}
function mintClientId() {
  return `client:${uuidv4()}`;
}
function findChapter(course, chapterId, label) {
  const chapter = course.chapters.find((item) => item.id === chapterId);
  if (!chapter) {
    throw new TrainingOperationError(
      `${label}: the training has no chapter ${chapterId}`
    );
  }
  return chapter;
}
function assertCourseHasLesson(course, lessonId, label) {
  const inCourse = course.chapters.some(
    (chapter) => chapter.lessons.some(
      (lesson) => lesson.interviewSectionDefinitionId === lessonId
    )
  );
  if (!inCourse) {
    throw new TrainingOperationError(
      `${label}: the training has no lesson ${lessonId}`
    );
  }
}
function findSection(training, lessonId, label) {
  const section = training.sections.find((item) => item.id === lessonId);
  if (!section) {
    throw new TrainingOperationError(
      `${label}: the training has no lesson ${lessonId}`
    );
  }
  return section;
}
function replaceChapter(course, chapter) {
  return {
    ...course,
    chapters: course.chapters.map(
      (item) => item.id === chapter.id ? chapter : item
    )
  };
}
function buildQuestions(questions) {
  return questions.map((question) => ({ question }));
}
function parseInstructions(instructions) {
  return parseMultilineContent(instructions, { parsesVariables: false });
}
function buildLessonSettings(lesson) {
  switch (lesson.lesson_type) {
    case InterviewSectionType.ChatQuiz:
      return {
        type: lesson.lesson_type,
        topic: lesson.topic,
        questions: buildQuestions(lesson.questions)
      };
    case InterviewSectionType.KnowledgeTest:
      return {
        type: lesson.lesson_type,
        title: lesson.title,
        questions: buildQuestions(lesson.questions)
      };
    case InterviewSectionType.RolePlay:
      return {
        type: lesson.lesson_type,
        title: lesson.title,
        participant_instructions: parseInstructions(
          lesson.participant_instructions
        ),
        agent_instructions: parseInstructions(lesson.agent_instructions)
      };
  }
}
function updateLessonSettings(section, changes, label) {
  const { settings } = section;
  const typeMismatch = new TrainingOperationError(
    `${label}: lesson ${section.id} is a ${settings.type} lesson, not a ${changes.lesson_type} lesson`
  );
  switch (changes.lesson_type) {
    case InterviewSectionType.ChatQuiz:
      if (settings.type !== InterviewSectionType.ChatQuiz) {
        throw typeMismatch;
      }
      return {
        ...settings,
        topic: changes.topic ?? settings.topic,
        questions: changes.questions ? buildQuestions(changes.questions) : settings.questions
      };
    case InterviewSectionType.KnowledgeTest:
      if (settings.type !== InterviewSectionType.KnowledgeTest) {
        throw typeMismatch;
      }
      return {
        ...settings,
        title: changes.title ?? settings.title,
        questions: changes.questions ? buildQuestions(changes.questions) : settings.questions
      };
    case InterviewSectionType.RolePlay:
      if (settings.type !== InterviewSectionType.RolePlay) {
        throw typeMismatch;
      }
      return {
        ...settings,
        title: changes.title ?? settings.title,
        participant_instructions: changes.participant_instructions !== void 0 ? parseInstructions(changes.participant_instructions) : settings.participant_instructions,
        agent_instructions: changes.agent_instructions !== void 0 ? parseInstructions(changes.agent_instructions) : settings.agent_instructions
      };
  }
}
function countTrainingOperations(operations) {
  const counts = {
    chapters: { added: 0, updated: 0, removed: 0 },
    lessons: { added: 0, updated: 0, removed: 0 },
    moved: 0
  };
  for (const operation of operations) {
    switch (operation.type) {
      case "add_chapter":
        counts.chapters.added += 1;
        counts.lessons.added += operation.lessons.length;
        break;
      case "update_chapter":
        counts.chapters.updated += 1;
        break;
      case "remove_chapter":
        counts.chapters.removed += 1;
        break;
      case "add_lesson":
        counts.lessons.added += 1;
        break;
      case "update_lesson":
        counts.lessons.updated += 1;
        break;
      case "remove_lesson":
        counts.lessons.removed += 1;
        break;
      case "move_chapter":
      case "move_lesson":
      case "move_lesson_to_chapter":
        counts.moved += 1;
        break;
    }
  }
  return counts;
}
const operationsSchema = trainingProposalOperationsSchema.describe(
  "Applied in order to the saved training. Reference only chapter and lesson ids from get-training-details. A new chapter's lessons go inline in its add_chapter."
);
const createTrainingProposalDataSchema = z.object({
  kind: z.literal("create_training"),
  name: z.string().min(1).max(200),
  playbook_id: playbookIdSchema.describe(
    "The playbook that grades the training's participants."
  ),
  operations: operationsSchema,
  summary: z.string().min(1).describe(
    "One plain sentence on what the training teaches. Shown on the proposal card."
  )
});
const updateTrainingProposalDataSchema = z.object({
  kind: z.literal("update_training"),
  operations: operationsSchema,
  summary: z.string().min(1).describe(
    "One plain sentence on what this proposal changes compared with the saved training. Shown on the proposal card."
  )
});
const proposeTrainingToolInputSchema = z.object({
  change: z.discriminatedUnion("kind", [
    createTrainingProposalDataSchema.extend({
      proposal_id: copilotProposalIdSchema.optional().describe(
        "When refining a training that has not been created yet, the pending create_training proposal to replace. Submit the whole revised draft. Omit only for a separate new training."
      )
    }),
    updateTrainingProposalDataSchema.extend({
      project_id: z.uuid().describe("The training's project id.")
    })
  ]).describe(
    "The change to propose: a whole new training (`create_training`), or changes to an existing one (`update_training`)."
  )
});
const proposeTrainingToolOutputSchema = z.object({
  proposal_id: copilotProposalIdSchema,
  project_id: z.uuid(),
  kind: trainingProposalKindSchema
});
export {
  TrainingOperationError,
  applyTrainingOperations,
  countTrainingOperations,
  proposeTrainingToolInputSchema,
  proposeTrainingToolOutputSchema,
  trainingProposalOperationsSchema
};
//# sourceMappingURL=trainingProposal.js.map
